import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronDown, Lock, Sparkles, WifiOff } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '#/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '#/components/ui/dropdown-menu'
import { api, ApiError, qk, useSubscription } from '#/lib/api'
import { MODELS, MODEL_ORDER, modelLabel, type ModelId } from '#/lib/ai/models'
import { m } from '#/paraglide/messages'
import { cn } from '#/lib/utils'

const badgeText = (b: string) => (b === 'fast' ? m.model_badge_fast() : b === 'balanced' ? m.model_badge_balanced() : b === 'deep' ? m.model_badge_deep() : b === 'premium' ? m.model_badge_premium() : m.model_badge_flagship())
const fmtWhen = (iso: string | null) => {
  if (!iso) return m.model_unavailable_soon()
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? m.model_unavailable_soon() : d.toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })
}

/**
 * Model selector (docs/PLAN_MODEL_SELECTOR.md §2.5). `value` null = "use my default".
 * Entitlement and live availability come from /api/subscription/status; the server re-checks everything.
 */
export function ModelPicker({ value, onChange, allowDefault, size = 'sm', className }: { value: ModelId | null; onChange: (id: ModelId | null) => void; allowDefault?: boolean; size?: 'sm' | 'md'; className?: string }) {
  const { data: sub } = useSubscription()
  const allowed = new Set(sub?.models.allowed ?? ['auto', 'gpt-5.6-terra', 'gpt-5.6-luna'])
  const unavailable = sub?.models.unavailable ?? {}
  const premium = sub?.models.premium
  const current = value ?? (allowDefault ? null : ((sub?.models.preferred as ModelId | null) ?? (sub?.models.default as ModelId | undefined) ?? 'gpt-5.6-terra'))
  const label = current ? (current === 'auto' ? m.model_auto() : modelLabel(current)) : m.model_plan_default()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={cn('gap-1.5 font-normal', size === 'sm' ? 'h-7 px-2 text-[11px]' : 'h-9 px-3 text-sm', className)} aria-label={m.model_pick_label()} data-testid="model-picker">
          <Sparkles className={size === 'sm' ? 'size-3' : 'size-4'} />
          <span className="truncate">{label}</span>
          <ChevronDown className="size-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {premium ? (premium.limit === -1 ? m.model_premium_pool_unlimited() : m.model_premium_pool({ used: Math.round(premium.used * 10) / 10, limit: premium.limit })) : m.model_title()}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {allowDefault ? (
          <DropdownMenuItem onClick={() => onChange(null)} className="text-sm">
            <span className="flex-1">{m.model_plan_default()}</span>
            {value === null ? <Check className="size-4" /> : null}
          </DropdownMenuItem>
        ) : null}
        {MODEL_ORDER.map((id) => {
          const def = id === 'auto' ? null : MODELS[id]
          const locked = !allowed.has(id)
          const down = unavailable[id]
          const disabled = locked || Boolean(down)
          return (
            <DropdownMenuItem key={id} disabled={disabled} onClick={() => !disabled && onChange(id)} className={cn('flex items-start gap-2 py-2', disabled && 'opacity-60')}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{id === 'auto' ? m.model_auto() : def!.label}</span>
                  {def ? <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{badgeText(def.badge)}</span> : null}
                  {def && def.weight > 0 ? <span className="text-[10px] text-muted-foreground">{m.model_premium_cost({ weight: def.weight })}</span> : null}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {id === 'auto' ? m.model_auto_desc() : locked ? (id === 'claude-fable-5' ? m.model_locked_fable() : m.model_locked()) : down ? (down.reason === 'quota' ? m.model_unavailable_quota({ when: fmtWhen(down.until) }) : m.model_unavailable_errors()) : def!.vendor}
                </div>
              </div>
              {locked ? <Lock className="mt-0.5 size-3.5 shrink-0" /> : down ? <WifiOff className="mt-0.5 size-3.5 shrink-0" /> : current === id ? <Check className="mt-0.5 size-4 shrink-0" /> : null}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Per-plan override chip: PATCH /api/plan/:id { model } and refresh the plan. */
export function PlanModelChip({ planId, value, className }: { planId: string; value: string | null | undefined; className?: string }) {
  const qc = useQueryClient()
  const v = (value ?? null) as ModelId | null
  const change = async (id: ModelId | null) => {
    try {
      await api(`/api/plan/${planId}`, { method: 'PATCH', json: { model: id } })
      qc.invalidateQueries({ queryKey: qk.plan(planId) })
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  return <ModelPicker value={v} onChange={change} allowDefault className={className} />
}

/** "Written with X" + optional downgrade note. `modelUsed` may be "a→b (reason)" from generation_job.model_used. */
export function ModelNote({ model, modelUsed, className }: { model?: string | null; modelUsed?: string | null; className?: string }) {
  const dg = modelUsed?.match(/^(.+?)→(.+?) \((\w+)\)$/)
  const shown = dg ? dg[2] : model ?? modelUsed?.split('+')[0]
  const lbl = modelLabel(shown)
  if (!lbl) return null
  const from = dg ? modelLabel(dg[1]) ?? dg[1]! : ''
  const note = dg ? (dg[3] === 'cap' ? m.model_downgraded_cap({ from, to: lbl }) : dg[3] === 'unavailable' ? m.model_downgraded_unavailable({ from, to: lbl }) : dg[3] === 'entitlement' ? m.model_downgraded_entitlement({ from, to: lbl }) : m.model_downgraded_error({ from, to: lbl })) : null
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground', className)}>
      <span className="inline-flex items-center gap-1"><Sparkles className="size-3" /> {m.model_made_with({ model: lbl })}</span>
      {note ? <span className="text-status-failed">{note}</span> : null}
    </span>
  )
}
